import { EXCEL_WRITE_CHUNK_SIZE } from './types'
import { yieldToEventLoop } from './batch-processing'
import { columnLettersToNumber, columnNumberToLetters } from './formula-utils'

// Read values from Excel in chunks to prevent freezing on large datasets
// Returns actual row numbers for each ticker (not just relative indices)
export const readValuesInChunks = async (
    context: Excel.RequestContext,
    worksheet: Excel.Worksheet,
    startingCell: string,
    chunkSize: number = 1000,
    onProgress?: (message: string) => void
): Promise<{
    nonBlankIndices: number[]
    nonBlankValues: string[][]
    tickerRows: number[]
}> => {
    const nonBlankIndices: number[] = []
    const nonBlankValues: string[][] = []
    const tickerRows: number[] = [] // Track actual row numbers for each ticker

    const cellMatch = startingCell.match(/([A-Z]+)(\d+)/)
    if (!cellMatch) return { nonBlankIndices, nonBlankValues, tickerRows }

    const column = cellMatch[1]
    const startingRow = parseInt(cellMatch[2])
    let currentRow = startingRow
    let foundEnd = false
    let chunkIndex = 0

    while (!foundEnd) {
        // Read a chunk of rows
        const rangeAddress = `${column}${currentRow}:${column}${
            currentRow + chunkSize - 1
        }`
        const chunkRange = worksheet.getRange(rangeAddress)
        chunkRange.load('values')
        chunkRange.load('formulas')
        await context.sync()

        const values = chunkRange.values
        const formulas = chunkRange.formulas

        // Process values in this chunk
        for (let i = 0; i < values.length; i++) {
            const cellValue = values[i][0]
            const cellFormula = formulas[i][0]
            const actualRow = currentRow + i

            // Skip cells that contain formulas - they're not ticker values
            if (
                typeof cellFormula === 'string' &&
                cellFormula.startsWith('=')
            ) {
                // Hit a formula cell - we're done (not a smart detection scenario)
                foundEnd = true
                break
            }

            if (cellValue && cellValue.toString().trim() !== '') {
                nonBlankIndices.push(nonBlankValues.length)
                tickerRows.push(actualRow) // Store actual row number
                nonBlankValues.push([cellValue])
            } else {
                // Hit a blank cell - we're done
                foundEnd = true
                break
            }
        }

        // If we processed all rows in chunk without hitting blank, continue
        if (!foundEnd && values.length === chunkSize) {
            currentRow += chunkSize
            chunkIndex++

            // Report progress
            if (onProgress) {
                onProgress(
                    `Reading tickers... (${nonBlankValues.length.toLocaleString()} found)`
                )
            }

            // Yield to event loop
            await yieldToEventLoop()
        } else {
            foundEnd = true
        }
    }

    return { nonBlankIndices, nonBlankValues, tickerRows }
}

// Read ALL tickers from a column (full column scan for refresh scenarios)
// Scans from startRow downward to find all tickers in the column
// This ensures we refresh all data cells, even those above the formula
export const readAllTickersFromColumn = async (
    context: Excel.RequestContext,
    worksheet: Excel.Worksheet,
    tickerColumn: string,
    startRow: number = 1, // Start from row 1 by default to catch all tickers
    maxRows: number = 100000, // Reasonable limit to prevent infinite scans
    chunkSize: number = 1000,
    onProgress?: (message: string) => void
): Promise<{
    nonBlankIndices: number[]
    nonBlankValues: string[][]
    tickerRows: number[]
}> => {
    const nonBlankIndices: number[] = []
    const nonBlankValues: string[][] = []
    const tickerRows: number[] = []

    let currentRow = startRow
    let foundEnd = false
    let consecutiveBlanks = 0
    const MAX_CONSECUTIVE_BLANKS = 10 // Stop after 10 consecutive blank rows

    while (!foundEnd && currentRow <= maxRows) {
        // Read a chunk of rows
        const endRow = Math.min(currentRow + chunkSize - 1, maxRows)
        const rangeAddress = `${tickerColumn}${currentRow}:${tickerColumn}${endRow}`
        const chunkRange = worksheet.getRange(rangeAddress)
        chunkRange.load('values')
        await context.sync()

        const values = chunkRange.values

        // Process values in this chunk
        for (let i = 0; i < values.length; i++) {
            const cellValue = values[i][0]
            const actualRow = currentRow + i

            if (cellValue && cellValue.toString().trim() !== '') {
                // Found a ticker
                consecutiveBlanks = 0
                nonBlankIndices.push(nonBlankValues.length)
                tickerRows.push(actualRow)
                nonBlankValues.push([cellValue])
            } else {
                // Blank cell
                consecutiveBlanks++
                // If we hit too many consecutive blanks, we're probably done
                if (consecutiveBlanks >= MAX_CONSECUTIVE_BLANKS) {
                    foundEnd = true
                    break
                }
            }
        }

        // If we processed all rows in chunk, continue
        if (
            !foundEnd &&
            values.length === chunkSize &&
            currentRow + chunkSize <= maxRows
        ) {
            currentRow += chunkSize

            // Report progress
            if (onProgress) {
                onProgress(
                    `Scanning tickers... (${nonBlankValues.length.toLocaleString()} found)`
                )
            }

            // Yield to event loop
            await yieldToEventLoop()
        } else {
            foundEnd = true
        }
    }

    return { nonBlankIndices, nonBlankValues, tickerRows }
}

// Read tickers from specific rows in a column (for refresh using stored data range)
export const readTickersFromRows = async (
    context: Excel.RequestContext,
    worksheet: Excel.Worksheet,
    tickerColumn: string,
    rows: number[],
    chunkSize: number = 1000
): Promise<{
    nonBlankIndices: number[]
    nonBlankValues: string[][]
    tickerRows: number[]
}> => {
    const nonBlankIndices: number[] = []
    const nonBlankValues: string[][] = []
    const tickerRows: number[] = []

    if (rows.length === 0)
        return { nonBlankIndices, nonBlankValues, tickerRows }

    // Sort rows to process in order
    const sortedRows = [...rows].sort((a, b) => a - b)

    // Process in chunks
    for (let i = 0; i < sortedRows.length; i += chunkSize) {
        const chunkRows = sortedRows.slice(i, i + chunkSize)
        const minRow = chunkRows[0]
        const maxRow = chunkRows[chunkRows.length - 1]

        const rangeAddress = `${tickerColumn}${minRow}:${tickerColumn}${maxRow}`
        const chunkRange = worksheet.getRange(rangeAddress)
        chunkRange.load('values')
        await context.sync()

        const values = chunkRange.values

        // Map values to rows
        for (let j = 0; j < chunkRows.length; j++) {
            const row = chunkRows[j]
            const valueIndex = row - minRow
            if (valueIndex >= 0 && valueIndex < values.length) {
                const cellValue = values[valueIndex][0]
                if (cellValue && cellValue.toString().trim() !== '') {
                    nonBlankIndices.push(nonBlankValues.length)
                    tickerRows.push(row)
                    nonBlankValues.push([cellValue])
                }
            }
        }

        // Yield to event loop
        await yieldToEventLoop()
    }

    return { nonBlankIndices, nonBlankValues, tickerRows }
}

// Write data to Excel in chunks to prevent freezing
// Uses batch range writes instead of row-by-row for better performance
// tickerRows: Array of actual row numbers where each ticker is located (ensures data writes to correct rows)
export const writeDataToExcelChunked = async (
    context: Excel.RequestContext,
    worksheet: Excel.Worksheet,
    data: any[][],
    nonBlankIndices: number[],
    wiseColumn: string,
    tickerRows: number[], // Array of actual row numbers for each ticker
    numColumns: number,
    applyOperation: ((value: any) => any) | null
): Promise<void> => {
    // Suspend calculation during bulk writes for better performance
    const application = context.workbook.application
    application.suspendApiCalculationUntilNextSync()

    // Helper to get the Excel range address for a single output row
    const getRowRangeAddress = (rowNumber: number) => {
        const startCol = wiseColumn
        const endColCode = columnLettersToNumber(wiseColumn) + numColumns - 1
        const endCol = columnNumberToLetters(endColCode)
        return `${startCol}${rowNumber}:${endCol}${rowNumber}`
    }

    // Write in chunks, but use one-range-per-row to correctly handle non-contiguous rows
    for (let i = 0; i < data.length; i += EXCEL_WRITE_CHUNK_SIZE) {
        const chunkEnd = Math.min(i + EXCEL_WRITE_CHUNK_SIZE, data.length)

        // Suspend calculation for this chunk
        application.suspendApiCalculationUntilNextSync()

        for (let dataIndex = i; dataIndex < chunkEnd; dataIndex++) {
            if (
                dataIndex < nonBlankIndices.length &&
                dataIndex < tickerRows.length
            ) {
                const dataRow = data[dataIndex]
                let processedRow = applyOperation
                    ? dataRow.map((value: any) => applyOperation(value))
                    : dataRow

                // Ensure the row width matches the expected number of columns
                if (processedRow.length > numColumns) {
                    processedRow = processedRow.slice(0, numColumns)
                } else if (processedRow.length < numColumns) {
                    const padded = new Array(numColumns).fill('')
                    for (let j = 0; j < processedRow.length; j++) {
                        padded[j] = processedRow[j]
                    }
                    processedRow = padded
                }

                const rowNumber = tickerRows[dataIndex]
                const rangeAddress = getRowRangeAddress(rowNumber)
                const rowRange = worksheet.getRange(rangeAddress)
                // values must be a 2D array; single row => [processedRow]
                rowRange.values = [processedRow]
            }
        }

        // Sync and yield after each chunk
        await context.sync()
        await yieldToEventLoop()
    }
}

// Write a single batch of results to Excel (for progressive writing)
// tickerRows: Array of actual row numbers where each ticker is located
// batchIndices: Indices into the tickerRows array for this batch
export const writeBatchResultsToExcel = async (
    context: Excel.RequestContext,
    worksheet: Excel.Worksheet,
    batchData: any[][],
    batchIndices: number[],
    wiseColumn: string,
    tickerRows: number[], // Array of actual row numbers for each ticker
    numColumns: number,
    applyOperation: ((value: any) => any) | null
): Promise<void> => {
    if (batchData.length === 0 || batchIndices.length === 0) return

    // Suspend calculation during write
    const application = context.workbook.application
    application.suspendApiCalculationUntilNextSync()

    // Build values array for this batch, ensuring each row matches numColumns
    const values: any[][] = []
    for (let i = 0; i < batchData.length && i < batchIndices.length; i++) {
        const dataRow = batchData[i]
        let processedRow = applyOperation
            ? dataRow.map((value: any) => applyOperation(value))
            : dataRow

        // Ensure the row width matches the expected number of columns
        if (processedRow.length > numColumns) {
            processedRow = processedRow.slice(0, numColumns)
        } else if (processedRow.length < numColumns) {
            const padded = new Array(numColumns).fill('')
            for (let j = 0; j < processedRow.length; j++) {
                padded[j] = processedRow[j]
            }
            processedRow = padded
        }

        values.push(processedRow)
    }

    if (values.length === 0) return

    // Helper to get the Excel range address for a single output row
    const getRowRangeAddress = (rowNumber: number) => {
        const startCol = wiseColumn
        const endColCode = columnLettersToNumber(wiseColumn) + numColumns - 1
        const endCol = columnNumberToLetters(endColCode)
        return `${startCol}${rowNumber}:${endCol}${rowNumber}`
    }

    // Write each row in the batch individually so we support non-contiguous rows
    for (let i = 0; i < values.length && i < batchIndices.length; i++) {
        const rowIndex = batchIndices[i]
        if (rowIndex < 0 || rowIndex >= tickerRows.length) continue

        const rowNumber = tickerRows[rowIndex]
        const rangeAddress = getRowRangeAddress(rowNumber)
        const rowRange = worksheet.getRange(rangeAddress)
        rowRange.values = [values[i]]
    }

    await context.sync()
    await yieldToEventLoop()
}
